import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Storage } from '../utils/storage';
import { playEatBeep, playDeathTone } from '../utils/audio';

type Phase = 'menu' | 'levelSelect' | 'playing' | 'paused' | 'gameOver' | 'highScores' | 'settings';
type AppScreen = 'gamesMenu' | 'game' | 'stats' | 'settings';
type CurrentGame = 'snake' | 'bounce' | 'brick' | null;
type Direction = 'up' | 'down' | 'left' | 'right';

export interface SnakeSegment {
  x: number;
  y: number;
}

type BouncePhase = 'menu' | 'playing' | 'paused' | 'gameOver';
type BrickPhase = 'menu' | 'playing' | 'paused' | 'gameOver';

export interface BouncePlatform {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BounceHazard {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BounceState {
  phase: BouncePhase;
  level: 1 | 2 | 3;
  score: number;
  bestScore: number;
  ballX: number;
  ballY: number;
  vx: number;
  vy: number;
  radius: number;
  inputDir: 'left' | 'right' | 'none';
  platforms: BouncePlatform[];
  spikes: BounceHazard[];
}

export interface BrickBlock {
  x: number;
  y: number;
  width: number;
  height: number;
  hitPoints: number;
  destroyed: boolean;
}

export interface BrickState {
  phase: BrickPhase;
  level: 1 | 2 | 3;
  score: number;
  bestScore: number;
  lives: number;
  paddleX: number;
  paddleWidth: number;
  ballX: number;
  ballY: number;
  ballVX: number;
  ballVY: number;
  ballStuckToPaddle: boolean;
  bricks: BrickBlock[];
}

interface DailyMission {
  target: 100;
  completed: boolean;
  resetDate: string;
}

interface GameState {
  // App-level state
  appScreen: AppScreen;
  currentGame: CurrentGame;

  // Snake game state (Game 1)
  phase: Phase;
  gridWidth: number;
  gridHeight: number;
  snake: SnakeSegment[];
  direction: Direction;
  nextDirection: Direction | null;
  food: { x: number; y: number } | null;
  score: number;
  bestScoreAllTime: number;
  bestScoreToday: number;
  totalGamesPlayed: number;
  currentLevel: 1 | 2 | 3;
  tickIntervalMs: number;
  lastTickTime: number;
  wrapMode: boolean;

  // Bounce game state (Game 2)
  bounce: BounceState;

  // Brick game state (Game 3)
  brick: BrickState;

  // Global per-game stats summary
  snakeGames: number;
  bounceGames: number;
  brickGames: number;

  // Progression
  dailyMission: DailyMission;
  achievements: string[];

  // Settings
  soundEnabled: boolean;
  colorblindMode: boolean;
  adsRemoved: boolean;

  // App-level actions
  openGamesMenu: () => void;
  openSnakeFromMenu: () => void;
  openBounceFromMenu: () => void;
  openBrickFromMenu: () => void;

  // Snake actions
  startGame: (level?: 1 | 2 | 3) => void;
  setDirection: (dir: Direction) => void;
  tick: () => void;
  pause: () => void;
  resume: () => void;
  gameOver: () => void;
  restart: () => void;
  goToMenu: () => void;
  goToLevelSelect: () => void;
  goToHighScores: () => void;

  // Bounce actions
  bounceStart: (level?: 1 | 2 | 3) => void;
  bounceTick: (dt: number) => void;
  bounceMove: (dir: 'left' | 'right' | 'none') => void;
  bouncePause: () => void;
  bounceResume: () => void;
  bounceGameOver: () => void;
  bounceBackToMenu: () => void;

  // Brick actions
  brickStart: (level?: 1 | 2 | 3) => void;
  brickTick: (dt: number) => void;
  brickMove: (dir: 'left' | 'right') => void;
  brickPause: () => void;
  brickResume: () => void;
  brickGameOver: () => void;
  brickBackToMenu: () => void;
  brickLaunchBall: () => void;

  // Shared settings / progression
  toggleSound: () => void;
  toggleColorblind: () => void;
  toggleWrapMode: () => void;
  completeMission: () => void;
  addAchievement: (id: string) => void;
  updateBestScores: () => void;
  resetDailyStatsIfNeeded: () => void;
  openSettings: () => void;
  closeSettings: () => void;
}

const GRID_WIDTH = 24;
const GRID_HEIGHT = 32;

const getInitialSnake = (): SnakeSegment[] => [
  { x: Math.floor(GRID_WIDTH / 2), y: Math.floor(GRID_HEIGHT / 2) },
  { x: Math.floor(GRID_WIDTH / 2) - 1, y: Math.floor(GRID_HEIGHT / 2) },
  { x: Math.floor(GRID_WIDTH / 2) - 2, y: Math.floor(GRID_HEIGHT / 2) }
];

const getLevelSpeed = (level: 1 | 2 | 3): number => {
  switch (level) {
    case 1: return 200; // Slow
    case 2: return 150; // Normal
    case 3: return 100; // Fast
    default: return 150;
  }
};

const spawnFood = (snake: SnakeSegment[], gridWidth: number, gridHeight: number): { x: number; y: number } => {
  const occupied = new Set(snake.map(s => `${s.x},${s.y}`));
  let x: number, y: number;
  do {
    x = Math.floor(Math.random() * gridWidth);
    y = Math.floor(Math.random() * gridHeight);
  } while (occupied.has(`${x},${y}`));
  return { x, y };
};

const today = () => new Date().toDateString();

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      appScreen: 'gamesMenu',
      currentGame: null,
      phase: 'menu',
      gridWidth: GRID_WIDTH,
      gridHeight: GRID_HEIGHT,
      snake: getInitialSnake(),
      direction: 'right',
      nextDirection: null,
      food: null,
      score: 0,
      bestScoreAllTime: Storage.get<number>('bestScoreAllTime', 0),
      bestScoreToday: Storage.get<number>('bestScoreToday', 0),
      totalGamesPlayed: Storage.get<number>('totalGamesPlayed', 0),
      currentLevel: Storage.get<1 | 2 | 3>('currentLevel', 2),
      tickIntervalMs: getLevelSpeed(Storage.get<1 | 2 | 3>('currentLevel', 2)),
      lastTickTime: 0,
      wrapMode: Storage.get<boolean>('wrapMode', false),

      bounce: {
        phase: 'menu',
        level: 1,
        score: 0,
        bestScore: Storage.get<number>('bounceBestScore', 0),
        ballX: 80,
        ballY: 60,
        vx: 0,
        vy: 0,
        radius: 6,
        inputDir: 'none',
        platforms: [],
        spikes: []
      },

      brick: {
        phase: 'menu',
        level: 1,
        score: 0,
        bestScore: Storage.get<number>('brickBestScore', 0),
        lives: 3,
        paddleX: 50,
        paddleWidth: 60,
        ballX: 50,
        ballY: 50,
        ballVX: 0,
        ballVY: 0,
        ballStuckToPaddle: true,
        bricks: []
      },

      snakeGames: Storage.get<number>('snakeGames', 0),
      bounceGames: Storage.get<number>('bounceGames', 0),
      brickGames: Storage.get<number>('brickGames', 0),
      
      dailyMission: (() => {
        const saved = Storage.get<DailyMission | null>('dailyMission', null);
        if (!saved || saved.resetDate !== today()) {
          const fresh: DailyMission = {
            target: 100,
            completed: false,
            resetDate: today()
          };
          Storage.set('dailyMission', fresh);
          return fresh;
        }
        return saved;
      })(),
      achievements: Storage.get<string[]>('achievements', []),
      soundEnabled: Storage.get<boolean>('soundEnabled', true),
      colorblindMode: Storage.get<boolean>('colorblindMode', false),
      adsRemoved: Storage.get<boolean>('adsRemoved', false),
      
      openGamesMenu: () => {
        set({
          appScreen: 'gamesMenu',
          currentGame: null,
          phase: 'menu'
        });
      },

      openSnakeFromMenu: () => {
        set({
          appScreen: 'game',
          currentGame: 'snake',
          phase: 'menu'
        });
      },

      openBounceFromMenu: () => {
        set({
          appScreen: 'game',
          currentGame: 'bounce',
          bounce: {
            ...get().bounce,
            phase: 'menu'
          }
        });
      },

      openBrickFromMenu: () => {
        set({
          appScreen: 'game',
          currentGame: 'brick',
          brick: {
            ...get().brick,
            phase: 'menu'
          }
        });
      },

      startGame: (level?: 1 | 2 | 3) => {
        const selectedLevel = level || get().currentLevel;
        const initialSnake = getInitialSnake();
        const initialFood = spawnFood(initialSnake, GRID_WIDTH, GRID_HEIGHT);
        const speed = getLevelSpeed(selectedLevel);
        
        set({
          appScreen: 'game',
          currentGame: 'snake',
          phase: 'playing',
          snake: initialSnake,
          direction: 'right',
          nextDirection: null,
          food: initialFood,
          score: 0,
          currentLevel: selectedLevel,
          tickIntervalMs: speed,
          lastTickTime: performance.now(),
          totalGamesPlayed: get().totalGamesPlayed + 1,
          snakeGames: get().snakeGames + 1
        });
        
        Storage.set('currentLevel', selectedLevel);
        Storage.set('totalGamesPlayed', get().totalGamesPlayed + 1);
        Storage.set('snakeGames', get().snakeGames + 1);
      },
      
      setDirection: (dir: Direction) => {
        const state = get();
        if (state.phase !== 'playing') return;
        
        // Prevent reversing into self (unless snake is length 1)
        const opposite: Record<Direction, Direction> = {
          up: 'down',
          down: 'up',
          left: 'right',
          right: 'left'
        };
        
        if (state.snake.length > 1 && dir === opposite[state.direction]) {
          return; // Ignore reversal
        }
        
        set({ nextDirection: dir });
      },
      
      tick: () => {
        const state = get();
        if (state.phase !== 'playing') return;
        
        const now = performance.now();
        if (now - state.lastTickTime < state.tickIntervalMs) return;
        
        // Apply buffered direction change
        let newDirection = state.direction;
        if (state.nextDirection) {
          newDirection = state.nextDirection;
          set({ direction: newDirection, nextDirection: null });
        }
        
        // Calculate new head position
        const head = state.snake[0];
        let newHead: SnakeSegment;
        
        switch (newDirection) {
          case 'up':
            newHead = { x: head.x, y: head.y - 1 };
            break;
          case 'down':
            newHead = { x: head.x, y: head.y + 1 };
            break;
          case 'left':
            newHead = { x: head.x - 1, y: head.y };
            break;
          case 'right':
            newHead = { x: head.x + 1, y: head.y };
            break;
        }
        
        // Wrap mode or wall collision
        if (state.wrapMode) {
          if (newHead.x < 0) newHead.x = GRID_WIDTH - 1;
          if (newHead.x >= GRID_WIDTH) newHead.x = 0;
          if (newHead.y < 0) newHead.y = GRID_HEIGHT - 1;
          if (newHead.y >= GRID_HEIGHT) newHead.y = 0;
        } else {
          // Wall collision = game over
          if (
            newHead.x < 0 ||
            newHead.x >= GRID_WIDTH ||
            newHead.y < 0 ||
            newHead.y >= GRID_HEIGHT
          ) {
            get().gameOver();
            return;
          }
        }
        
        // Self collision = game over
        for (const segment of state.snake) {
          if (segment.x === newHead.x && segment.y === newHead.y) {
            get().gameOver();
            return;
          }
        }
        
        // Check food collision
        const ateFood = state.food && newHead.x === state.food.x && newHead.y === state.food.y;
        
        let newSnake: SnakeSegment[];
        let newFood = state.food;
        let newScore = state.score;
        
        if (ateFood) {
          // Grow snake (add new head, keep tail)
          newSnake = [newHead, ...state.snake];
          newFood = spawnFood([newHead, ...state.snake], GRID_WIDTH, GRID_HEIGHT);
          newScore = state.score + 10;
          
          // Play eat sound
          playEatBeep();
          
          // Speed up slightly as score increases
          const speedBoost = Math.floor(newScore / 50);
          const baseSpeed = getLevelSpeed(state.currentLevel);
          const newTickInterval = Math.max(50, baseSpeed - speedBoost * 5);
          
          set({
            snake: newSnake,
            food: newFood,
            score: newScore,
            tickIntervalMs: newTickInterval,
            lastTickTime: now
          });
          
          // Check achievements
          if (newScore >= 100 && !state.achievements.includes('score-100')) {
            get().addAchievement('score-100');
          }
          if (newScore >= 200 && !state.achievements.includes('score-200')) {
            get().addAchievement('score-200');
          }
          if (newSnake.length >= 50 && !state.achievements.includes('length-50')) {
            get().addAchievement('length-50');
          }
        } else {
          // Move snake (add new head, remove tail)
          newSnake = [newHead, ...state.snake.slice(0, -1)];
          set({
            snake: newSnake,
            direction: newDirection,
            lastTickTime: now
          });
        }
      },
      
      pause: () => {
        if (get().phase === 'playing') {
          set({ phase: 'paused' });
        }
      },
      
      resume: () => {
        if (get().phase === 'paused') {
          set({ phase: 'playing', lastTickTime: performance.now() });
        }
      },
      
      gameOver: () => {
        const state = get();
        const newBestAllTime = Math.max(state.score, state.bestScoreAllTime);
        const newBestToday = Math.max(state.score, state.bestScoreToday);
        
        // Play death sound
        playDeathTone();
        
        if (state.score >= state.dailyMission.target && !state.dailyMission.completed) {
          get().completeMission();
        }
        
        get().updateBestScores();
        
        set({
          phase: 'gameOver',
          bestScoreAllTime: newBestAllTime,
          bestScoreToday: newBestToday
        });
      },
      
      restart: () => {
        set({
          appScreen: 'game',
          currentGame: 'snake',
          phase: 'menu',
          score: 0,
          snake: getInitialSnake(),
          direction: 'right',
          nextDirection: null,
          food: null
        });
      },
      
      goToMenu: () => set({ phase: 'menu' }),
      goToLevelSelect: () => set({ phase: 'levelSelect' }),
      goToHighScores: () => set({ phase: 'highScores' }),

      // Bounce (Game 2)
      bounceStart: (level?: 1 | 2 | 3) => {
        const lvl: 1 | 2 | 3 = level || 1;
        const width = 160;
        const height = 120;
        // simple static platforms/spikes per level
        const basePlatform: BouncePlatform = {
          x: 10,
          y: height - 20,
          width: width - 20,
          height: 12
        };
        const extraPlatforms: BouncePlatform[] =
          lvl === 1
            ? []
            : lvl === 2
            ? [
                { x: 40, y: height - 120, width: 80, height: 10 },
                { x: width - 140, y: height - 180, width: 80, height: 10 }
              ]
            : [
                { x: 40, y: height - 120, width: 60, height: 10 },
                { x: width / 2 - 40, y: height - 200, width: 80, height: 10 },
                { x: width - 120, y: height - 260, width: 50, height: 10 }
              ];
        const spikes: BounceHazard[] =
          lvl === 1
            ? []
            : lvl === 2
            ? [{ x: width / 2 - 40, y: height - 40, width: 80, height: 10 }]
            : [
                { x: width / 3 - 30, y: height - 40, width: 60, height: 10 },
                { x: (2 * width) / 3 - 30, y: height - 40, width: 60, height: 10 }
              ];

        set((state) => ({
          appScreen: 'game',
          currentGame: 'bounce',
          bounce: {
            ...state.bounce,
            phase: 'playing',
            level: lvl,
            score: 0,
            ballX: width / 2,
            ballY: height - 40,
            vx: 0,
            vy: -40, // upward initial velocity (world units/s)
            radius: 6,
            inputDir: 'none',
            platforms: [basePlatform, ...extraPlatforms],
            spikes
          },
          bounceGames: state.bounceGames + 1
        }));
        Storage.set('bounceGames', get().bounceGames + 1);
      },

      bounceTick: (dt: number) => {
        const { bounce } = get();
        if (bounce.phase !== 'playing') return;

        let { ballX, ballY, vx, vy, radius, platforms, spikes, score, bestScore, level } =
          bounce;

        const width = 160;
        const height = 120;

        // Per-level parameters
        const gravity = level === 1 ? 80 : level === 2 ? 100 : 120;
        const moveSpeed = level === 1 ? 40 : level === 2 ? 55 : 70;
        const bounceVel = level === 1 ? 80 : level === 2 ? 90 : 110;

        // Horizontal input
        if (bounce.inputDir === 'left') {
          vx = -moveSpeed;
        } else if (bounce.inputDir === 'right') {
          vx = moveSpeed;
        } else {
          vx *= 0.9; // friction
          if (Math.abs(vx) < 5) vx = 0;
        }

        const prevY = ballY;

        // Integrate
        vy += gravity * dt;
        ballY += vy * dt;
        ballX += vx * dt;

        // horizontal bounds
        if (ballX - radius < 0) {
          ballX = radius;
          vx = 0;
        } else if (ballX + radius > width) {
          ballX = width - radius;
          vx = 0;
        }

        // Platform collision (only when falling and crossing top)
        for (const p of platforms) {
          const onTop =
            vy > 0 &&
            prevY + radius <= p.y &&
            ballY + radius >= p.y &&
            ballX >= p.x &&
            ballX <= p.x + p.width;
          if (onTop) {
            ballY = p.y - radius;
            vy = -bounceVel;
            score += level; // faster score on higher levels
            if (score > bestScore) {
              bestScore = score;
              Storage.set('bounceBestScore', bestScore);
            }
          }
        }

        // Spike collision
        for (const s of spikes) {
          const hit =
            ballX >= s.x &&
            ballX <= s.x + s.width &&
            ballY + radius >= s.y &&
            ballY - radius <= s.y + s.height;
          if (hit) {
            get().bounceGameOver();
            return;
          }
        }

        // Fell off screen
        if (ballY - radius > height + 10) {
          get().bounceGameOver();
          return;
        }

        set((state) => ({
          bounce: {
            ...state.bounce,
            ballX,
            ballY,
            vx,
            vy,
            score,
            bestScore
          }
        }));
      },

      bounceMove: (dir: 'left' | 'right' | 'none') => {
        set((state) => {
          if (state.bounce.phase !== 'playing') return state;
          return {
            bounce: {
              ...state.bounce,
              inputDir: dir
            }
          };
        });
      },

      bouncePause: () => {
        set((state) =>
          state.bounce.phase === 'playing'
            ? { bounce: { ...state.bounce, phase: 'paused' } }
            : state
        );
      },

      bounceResume: () => {
        set((state) =>
          state.bounce.phase === 'paused'
            ? { bounce: { ...state.bounce, phase: 'playing' } }
            : state
        );
      },

      bounceGameOver: () => {
        set((state) => {
          const best = Math.max(state.bounce.score, state.bounce.bestScore);
          Storage.set('bounceBestScore', best);
          return {
            bounce: { ...state.bounce, phase: 'gameOver', bestScore: best }
          };
        });
      },

      bounceBackToMenu: () => {
        set((state) => ({
          bounce: { ...state.bounce, phase: 'menu' }
        }));
      },

      // Brick (Game 3)
      brickStart: (level?: 1 | 2 | 3) => {
        const lvl: 1 | 2 | 3 = level || 1;
        const width = 160;
        const height = 120;
        const paddleWidth = lvl === 1 ? 40 : lvl === 2 ? 30 : 24;

        // simple brick layout per level
        const bricks: BrickBlock[] = [];
        const rows = lvl === 1 ? 3 : lvl === 2 ? 4 : 5;
        const cols = 7;
        const brickW = (width - 40) / cols;
        const brickH = 8;
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const x = 20 + c * brickW;
            const y = 20 + r * (brickH + 4);
            let hitPoints = 1;
            if (lvl >= 2 && (r + c) % 3 === 0) {
              hitPoints = 2; // hard brick
            }
            bricks.push({
              x,
              y,
              width: brickW - 2,
              height: brickH,
              hitPoints,
              destroyed: false
            });
          }
        }

        set((state) => ({
          appScreen: 'game',
          currentGame: 'brick',
          brick: {
            ...state.brick,
            phase: 'playing',
            level: lvl,
            score: 0,
            lives: 3,
            paddleX: width / 2 - paddleWidth / 2,
            paddleWidth,
            ballX: width / 2,
            ballY: height - 15,
            ballVX: 0,
            ballVY: 0,
            ballStuckToPaddle: true,
            bricks
          },
          brickGames: state.brickGames + 1
        }));
        Storage.set('brickGames', get().brickGames + 1);
      },

      brickTick: (dt: number) => {
        const { brick } = get();
        if (brick.phase !== 'playing') return;

        let {
          ballX,
          ballY,
          ballVX,
          ballVY,
          paddleX,
          paddleWidth,
          bricks,
          score,
          bestScore,
          lives,
          ballStuckToPaddle
        } = brick;

        const width = 160;
        const height = 120;
        const radius = 3;

        const level = brick.level;
        const BALL_SPEED = level === 1 ? 80 : level === 2 ? 100 : 120;
        const BALL_MAX_ANGLE_SPEED = level === 1 ? 80 : level === 2 ? 100 : 120;
        const paddleY = height - 12;

        // Paddle bounds from any external moves
        if (paddleX < 0) paddleX = 0;
        if (paddleX + paddleWidth > width) paddleX = width - paddleWidth;

        if (brick.ballStuckToPaddle) {
          // Keep ball on paddle until launch
          ballX = paddleX + paddleWidth / 2;
          ballY = paddleY - radius - 1;
        } else {
          // Integrate ball
          ballX += ballVX * dt;
          ballY += ballVY * dt;

          // Wall collisions
          if (ballX - radius < 0) {
            ballX = radius;
            ballVX = Math.abs(ballVX);
          } else if (ballX + radius > width) {
            ballX = width - radius;
            ballVX = -Math.abs(ballVX);
          }
          if (ballY - radius < 0) {
            ballY = radius;
            ballVY = Math.abs(ballVY);
          }

          // Paddle collision (downward)
          const withinPaddleX = ballX >= paddleX && ballX <= paddleX + paddleWidth;
          const nearPaddleY =
            ballY + radius >= paddleY && ballY - radius <= paddleY + 4;
          if (ballVY > 0 && withinPaddleX && nearPaddleY) {
            ballY = paddleY - radius - 0.5;
            const hit =
              (ballX - (paddleX + paddleWidth / 2)) / (paddleWidth / 2 || 1);
            ballVY = -Math.abs(BALL_SPEED);
            ballVX = hit * BALL_MAX_ANGLE_SPEED;
          }

          // Brick collisions (AABB, one brick per frame)
          for (let i = 0; i < bricks.length; i++) {
            const b = bricks[i];
            if (b.destroyed) continue;

            const withinX =
              ballX + radius >= b.x && ballX - radius <= b.x + b.width;
            const withinY =
              ballY + radius >= b.y && ballY - radius <= b.y + b.height;

            if (withinX && withinY) {
              // Determine penetration
              const overlapLeft = ballX + radius - b.x;
              const overlapRight = b.x + b.width - (ballX - radius);
              const overlapTop = ballY + radius - b.y;
              const overlapBottom = b.y + b.height - (ballY - radius);

              const minOverlapX = Math.min(overlapLeft, overlapRight);
              const minOverlapY = Math.min(overlapTop, overlapBottom);

              if (minOverlapX < minOverlapY) {
                // Horizontal bounce
                ballVX = -ballVX;
                const shift = minOverlapX + 0.1;
                if (overlapLeft < overlapRight) {
                  ballX -= shift;
                } else {
                  ballX += shift;
                }
              } else {
                // Vertical bounce
                ballVY = -ballVY;
                const shift = minOverlapY + 0.1;
                if (overlapTop < overlapBottom) {
                  ballY -= shift;
                } else {
                  ballY += shift;
                }
              }

              const newHP = b.hitPoints - 1;
              bricks[i] = {
                ...b,
                hitPoints: newHP,
                destroyed: newHP <= 0
              };

              score += b.hitPoints > 1 ? 15 : 10;
              if (score > bestScore) {
                bestScore = score;
                Storage.set('brickBestScore', bestScore);
              }

              break;
            }
          }

          // Ball lost
          if (ballY - radius > height) {
            lives -= 1;
            if (lives <= 0) {
              get().brickGameOver();
              return;
            }
            // reset to paddle
            ballStuckToPaddle = true;
            ballVX = 0;
            ballVY = 0;
            ballX = paddleX + paddleWidth / 2;
            ballY = paddleY - radius - 1;
          }
        }

        // Level complete: all bricks destroyed
        const allDestroyed = bricks.every((b) => b.destroyed);
        if (allDestroyed) {
          const nextLevel: 1 | 2 | 3 =
            brick.level === 3 ? 1 : ((brick.level + 1) as 1 | 2 | 3);
          get().brickStart(nextLevel);
          return;
        }

        set((state) => ({
          brick: {
            ...state.brick,
            paddleX,
            ballX,
            ballY,
            ballVX,
            ballVY,
            ballStuckToPaddle,
            bricks,
            score,
            bestScore,
            lives
          }
        }));
      },

      brickMove: (dir: 'left' | 'right') => {
        set((state) => {
          if (state.brick.phase !== 'playing') return state;
          const width = 160;
          const speed = 80; // units per second, scaled in tick via dt
          let newX =
            state.brick.paddleX + (dir === 'left' ? -speed : speed) * 0.05;
          newX = Math.max(0, Math.min(width - state.brick.paddleWidth, newX));
          return {
            brick: {
              ...state.brick,
              paddleX: newX
            }
          };
        });
      },

      brickPause: () => {
        set((state) =>
          state.brick.phase === 'playing'
            ? { brick: { ...state.brick, phase: 'paused' } }
            : state
        );
      },

      brickResume: () => {
        set((state) =>
          state.brick.phase === 'paused'
            ? { brick: { ...state.brick, phase: 'playing' } }
            : state
        );
      },

      brickGameOver: () => {
        set((state) => {
          const best = Math.max(state.brick.score, state.brick.bestScore);
          Storage.set('brickBestScore', best);
          return {
            brick: { ...state.brick, phase: 'gameOver', bestScore: best }
          };
        });
      },

      brickBackToMenu: () => {
        set((state) => ({
          brick: { ...state.brick, phase: 'menu' }
        }));
      },

      brickLaunchBall: () => {
        set((state) => {
          if (!state.brick.ballStuckToPaddle || state.brick.phase !== 'playing') {
            return state;
          }
          const level = state.brick.level;
          const BALL_SPEED = level === 1 ? 80 : level === 2 ? 100 : 120;
          return {
            brick: {
              ...state.brick,
              ballStuckToPaddle: false,
              ballVX: 0,
              ballVY: -BALL_SPEED
            }
          };
        });
      },
      
      toggleSound: () => {
        const next = !get().soundEnabled;
        Storage.set('soundEnabled', next);
        set({ soundEnabled: next });
      },
      
      toggleColorblind: () => {
        const next = !get().colorblindMode;
        Storage.set('colorblindMode', next);
        set({ colorblindMode: next });
      },
      
      toggleWrapMode: () => {
        const next = !get().wrapMode;
        Storage.set('wrapMode', next);
        set({ wrapMode: next });
      },
      
      completeMission: () => {
        const updated: DailyMission = {
          ...get().dailyMission,
          completed: true
        };
        Storage.set('dailyMission', updated);
        set({ dailyMission: updated });
      },
      
      addAchievement: (id: string) => {
        const current = get().achievements;
        if (current.includes(id)) return;
        const updated = [...current, id];
        Storage.set('achievements', updated);
        set({ achievements: updated });
      },
      
      updateBestScores: () => {
        const state = get();
        const newBestAllTime = Math.max(state.score, state.bestScoreAllTime);
        const newBestToday = Math.max(state.score, state.bestScoreToday);
        Storage.set('bestScoreAllTime', newBestAllTime);
        Storage.set('bestScoreToday', newBestToday);
        set({
          bestScoreAllTime: newBestAllTime,
          bestScoreToday: newBestToday
        });
      },
      
      resetDailyStatsIfNeeded: () => {
        const state = get();
        const todayStr = today();
        if (state.dailyMission.resetDate !== todayStr) {
          const fresh: DailyMission = {
            target: 100,
            completed: false,
            resetDate: todayStr
          };
          Storage.set('dailyMission', fresh);
          Storage.set('bestScoreToday', 0);
          set({
            dailyMission: fresh,
            bestScoreToday: 0
          });
        }
      },
      
      openSettings: () => set({ phase: 'settings' }),
      closeSettings: () => set((state) => ({
        phase: state.phase === 'settings' ? 'menu' : state.phase
      }))
    }),
    {
      name: 'snake-nostalgia-storage',
      partialize: (state) => ({
        appScreen: state.appScreen,
        currentGame: state.currentGame,
        bestScoreAllTime: state.bestScoreAllTime,
        bestScoreToday: state.bestScoreToday,
        totalGamesPlayed: state.totalGamesPlayed,
         snakeGames: state.snakeGames,
         bounceGames: state.bounceGames,
         brickGames: state.brickGames,
         bounceBestScore: state.bounce.bestScore,
         brickBestScore: state.brick.bestScore,
        currentLevel: state.currentLevel,
        achievements: state.achievements,
        soundEnabled: state.soundEnabled,
        colorblindMode: state.colorblindMode,
        adsRemoved: state.adsRemoved,
        wrapMode: state.wrapMode,
        dailyMission: state.dailyMission
      })
    }
  )
);
